<template>
  <div class="chat-receiver" ref="messageContainer" @click="clickLink">
    <div class="media receiver ml-auto" :data-message-id="message.id">
      <div v-if="!message.file" class="media-body" :class="{'no-avatar': !showArrow}">
        <div class="bg-universe rounded text-right py-2 px-3 mb-1" :class="{'arrow-container': showArrow}">
          <div class="text-small mb-0 text-white" v-html="message.content"></div>
          <div v-if="showArrow" class="arrow"></div>
        </div>
      </div>

      <div v-else class="preview-file media-body" :class="{'no-avatar': !showArrow}" :data-message-id="message.id">
        <div class="bg-universe rounded text-right p-3 mb-1" :class="{'arrow-container': showArrow}">
          <a :href="getMessagePreviewUrl(message.id, message.file_mime)" target="_blank">
            <video
              v-if="message.file_mime.startsWith('video/')"
              controls
              class="chat-video"
              preload="metadata"
              :src="getMessagePreviewUrl(message.id, message.file_mime)"
            >
              Your browser does not support the video tag.
            </video>
            <img
              v-else
              :alt="message.file_name"
              :src="getIconForFileType(message.file_mime)"
              :class="message.file_mime.startsWith('image/') ? 'image' : ''"
              :style="{ height: message.file_mime.startsWith('image/') ? imgHeight : 'auto' }"
            />

            <div v-if="message.file_name" :title="message.file_name">
              {{ formattedFileName }}
            </div>
          </a>
          <div v-if="showArrow" class="arrow"></div>
        </div>
      </div>

      <img v-if="avatar && showArrow" :src="avatar" :alt="message.sender.name">
    </div>

    <!-- Show time if specified -->
    <p v-if="showTime" class="chat-timestamp small text-muted">
      {{ formatMessageTime(message.created_at) }}
    </p>
  </div>
</template>

<script>
import { formatDateTime, formatFileName, isSafari } from "../../../utils/helpers";

export default {
  name: 'ChatMessageReceiver',
  props: {
    isFloating: {
      type: Boolean,
      default: true
    },
    avatar: {
      type: String,
      default: ''
    },
    message: {
      type: [Array, Object],
      default: []
    },
    showTime: {
      type: Boolean,
      default: false
    },
    showArrow: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoadingPreview: false
    }
  },
  computed: {
    imgHeight() {
      return this.isFloating ? '150px' : 'auto';
    },
    formattedFileName() {
      return formatFileName(this.message.file_name);
    }
  },
  methods: {
    formatMessageTime(created_at) {
      return formatDateTime(created_at);
    },
    clickLink(event) {
      const anchorElement = event.target.closest('a');

      if (anchorElement && anchorElement.closest('.bg-universe')) {
        event.preventDefault();
        window.open(anchorElement.href, '_blank');
      }
    },
    getIconForFileType(file_mime) {
      if (file_mime.startsWith('image/')) {
        return this.getMessagePreviewUrl(this.message.id, file_mime);
      } else if (file_mime === 'application/pdf') {
        return '/images/icons-file/pdf.png';
      } else if (file_mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        return '/images/icons-file/docx.png';
      } else if (file_mime.startsWith('video/')) {
        return '/images/icons-file/video.png';
      }
    },
    getMessagePreviewUrl(message_id, file_mime) {
      try {
        this.isLoadingPreview = true;
        const baseURL = window.location.origin + '/admin';
        const previewURL = '/api/uni-chat/message/' + message_id + '/preview';

        // If the browser is Safari and the file is a video, return the download link
        if (isSafari() && file_mime && file_mime.startsWith('video/')) {
          return this.getVideoDownloadLink(message_id);
        }

        return baseURL + previewURL;
      } catch (error) {
        console.error("Error loading preview:", error);
      } finally {
        this.isLoadingPreview = false;
      }
    },
    getVideoDownloadLink(message_id) {
      try {
        const baseURL = window.location.origin + '/admin';
        const downloadURL = '/api/uni-chat/message/' + message_id + '/download';
        return baseURL + downloadURL;
      } catch (error) {
        console.error("Error generating download link for video:", error);
        return '';
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.arrow-container {
  position: relative;

  .arrow {
    position: absolute;
    top: 0;
    right: -10px;
    border: 10px solid transparent;
    border-top: 10px solid #009F91;
  }
}

.preview-file {
  margin: 0 !important;

  .chat-video {
    width: 100%;
  }

  img {
    width: auto;
    object-fit: cover !important;
    border: none;
    background-color: transparent;
    border-radius: 14px !important;

    &.image {
      width: 100% !important;
    }
  }
}

.no-avatar {
  padding-right: 15px;
}
</style>
